import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLang } from "../LangContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsArticle() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const nav = useNavigate();
    const [item, setItem] = useState(null);
    const [other, setOther] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notfound, setNotfound] = useState(false);

    // Multilingual field picker with EN fallback (tolerates legacy plain strings)
    const pick = (f) => (f && typeof f === "object" ? (f[lang.toLowerCase()] || f.en || "") : f || "");

    useEffect(() => {
        setLoading(true);
        setNotfound(false);
        axios
            .get(`${API}/news/${slug}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotfound(true))
            .finally(() => setLoading(false));
        axios.get(`${API}/news`).then((r) => setOther(r.data)).catch(() => {});
        window.scrollTo(0, 0);
    }, [slug]);

    return (
        <div className="bg-ink text-white grain min-h-screen" data-testid="news-article-page">
            <Header />
            <main className="pt-32 md:pt-40 px-6 md:px-12">
                <div className="max-w-4xl mx-auto">
                    <Link
                        to="/news"
                        data-testid="news-back"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-volt"
                    >
                        <ArrowLeft size={14} /> {t.news.back}
                    </Link>
                </div>

                {loading && (
                    <div className="max-w-4xl mx-auto mt-20 text-zinc-500 flex items-center gap-3">
                        <Loader2 className="animate-spin" size={18} /> Loading…
                    </div>
                )}

                {notfound && (
                    <div className="max-w-4xl mx-auto mt-20">
                        <h1 className="font-graffiti text-5xl text-white">Article not found</h1>
                        <p className="text-zinc-400 mt-4">It may have been moved or unpublished.</p>
                        <button
                            onClick={() => nav("/")}
                            className="btn-volt mt-8 px-6 py-3 text-xs"
                        >
                            ← Back home
                        </button>
                    </div>
                )}

                {item && (
                    <article className="max-w-4xl mx-auto mt-8 pb-24">
                        <div className="text-xs font-mono uppercase tracking-[0.3em] text-volt mb-4">
                            {item.date}
                        </div>
                        <h1
                            data-testid="news-article-title"
                            className="font-graffiti text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight"
                        >
                            {pick(item.title)}
                        </h1>
                        <p className="mt-6 text-xl text-zinc-300 leading-relaxed">{pick(item.excerpt)}</p>

                        {item.image_url && (
                            <div className="mt-12 aspect-[16/9] w-full overflow-hidden border border-white/10">
                                <img src={item.image_url} alt={pick(item.title)} className="w-full h-full object-cover" />
                            </div>
                        )}

                        <div data-testid="news-article-body" className="mt-12 text-lg text-zinc-300 leading-relaxed whitespace-pre-line">
                            {pick(item.body)}
                        </div>

                        {item.external_url && (
                            <a
                                href={item.external_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-volt mt-12 inline-flex items-center gap-2 px-6 py-3 text-xs"
                            >
                                Read original <ArrowUpRight size={14} />
                            </a>
                        )}

                        {other.filter((n) => n.slug !== slug).length > 0 && (
                            <div className="mt-24 pt-12 border-t border-white/10">
                                <div className="text-xs font-mono uppercase tracking-[0.3em] text-volt mb-6">
                                    — More from Urban Rookies
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {other
                                        .filter((n) => n.slug !== slug)
                                        .slice(0, 2)
                                        .map((n) => (
                                            <Link
                                                key={n.id}
                                                to={`/news/${n.slug}`}
                                                className="border border-white/10 p-6 hover:border-volt transition-colors group"
                                            >
                                                <div className="text-xs font-mono text-volt mb-2">{n.date}</div>
                                                <h3 className="font-display font-bold text-white group-hover:text-volt transition-colors">
                                                    {pick(n.title)}
                                                </h3>
                                            </Link>
                                        ))}
                                </div>
                            </div>
                        )}
                    </article>
                )}
            </main>
            <Footer />
        </div>
    );
}
